/**
 * Comprehensive Health Check System for Wixell Platform
 * Monitors application health, dependencies, and system resources
 */

class HealthCheckSystem {
  constructor() {
    this.checks = new Map();
    this.results = new Map();
    this.isRunning = false;
    this.interval = null;
    this.checkInterval = 60000; // 1 minute
    this.timeout = 10000; // 10 seconds per check
    
    this.init();
  }

  init() {
    // Register default health checks
    this.registerDefaultChecks();
    
    // Start monitoring
    this.start();
  }

  registerDefaultChecks() {
    // Application health checks
    this.registerCheck('application', {
      name: 'Application Status',
      description: 'Basic application health',
      timeout: 5000,
      check: this.checkApplicationHealth.bind(this)
    });

    // Authentication health check
    this.registerCheck('authentication', {
      name: 'Authentication Service',
      description: 'Authentication system health',
      timeout: 5000,
      check: this.checkAuthenticationHealth.bind(this)
    });

    // Database health check
    this.registerCheck('database', {
      name: 'Database Connection',
      description: 'Database connectivity and performance',
      timeout: 10000,
      check: this.checkDatabaseHealth.bind(this)
    });

    // API health check
    this.registerCheck('api', {
      name: 'API Endpoints',
      description: 'API endpoint availability and response times',
      timeout: 8000,
      check: this.checkAPIHealth.bind(this)
    });

    // Performance health check
    this.registerCheck('performance', {
      name: 'Performance Metrics',
      description: 'Application performance indicators',
      timeout: 3000,
      check: this.checkPerformanceHealth.bind(this)
    });

    // Security health check
    this.registerCheck('security', {
      name: 'Security Status',
      description: 'Security configurations and threats',
      timeout: 5000,
      check: this.checkSecurityHealth.bind(this)
    });

    // Resource health check
    this.registerCheck('resources', {
      name: 'System Resources',
      description: 'Memory and CPU usage',
      timeout: 3000,
      check: this.checkResourceHealth.bind(this)
    });

    // External dependencies
    this.registerCheck('external', {
      name: 'External Services',
      description: 'Third-party service availability',
      timeout: 8000,
      check: this.checkExternalServicesHealth.bind(this)
    });
  }

  registerCheck(id, config) {
    this.checks.set(id, {
      id,
      name: config.name,
      description: config.description,
      timeout: config.timeout || this.timeout,
      check: config.check,
      enabled: config.enabled !== false,
      critical: config.critical || false,
      interval: config.interval || this.checkInterval
    });
  }

  async runCheck(checkId) {
    const check = this.checks.get(checkId);
    if (!check || !check.enabled) {
      return null;
    }

    const startTime = performance.now();
    
    try {
      const result = await Promise.race([
        check.check(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Health check timeout')), check.timeout)
        )
      ]);

      const duration = performance.now() - startTime;

      return {
        checkId,
        name: check.name,
        status: 'healthy',
        message: result.message || 'Check passed',
        details: result.details || {},
        duration: Math.round(duration),
        timestamp: new Date().toISOString(),
        critical: check.critical
      };

    } catch (error) {
      const duration = performance.now() - startTime;

      return {
        checkId,
        name: check.name,
        status: 'unhealthy',
        message: error.message || 'Check failed',
        details: { error: error.message, stack: error.stack },
        duration: Math.round(duration),
        timestamp: new Date().toISOString(),
        critical: check.critical
      };
    }
  }

  async runAllChecks() {
    const promises = Array.from(this.checks.keys()).map(checkId => 
      this.runCheck(checkId)
    );

    const results = await Promise.allSettled(promises);
    
    const checkResults = {};
    results.forEach((result, index) => {
      const checkId = Array.from(this.checks.keys())[index];
      if (result.status === 'fulfilled') {
        checkResults[checkId] = result.value;
      } else {
        checkResults[checkId] = {
          checkId,
          name: this.checks.get(checkId)?.name || checkId,
          status: 'error',
          message: 'Check execution failed',
          details: { error: result.reason.message },
          duration: 0,
          timestamp: new Date().toISOString(),
          critical: this.checks.get(checkId)?.critical || false
        };
      }
    });

    this.results = new Map(Object.entries(checkResults));
    return checkResults;
  }

  // Individual health check implementations
  async checkApplicationHealth() {
    const checks = {
      domReady: document.readyState === 'complete',
      scriptsLoaded: document.scripts.length > 0,
      stylesLoaded: document.styleSheets.length > 0,
      hasTitle: !!document.title,
      hasMeta: !!document.querySelector('meta[name=description]'),
      hasFavicon: !!document.querySelector('link[rel*="icon"]')
    };

    const allPassed = Object.values(checks).every(Boolean);
    
    return {
      message: allPassed ? 'Application is healthy' : 'Application has issues',
      details: checks
    };
  }

  async checkAuthenticationHealth() {
    try {
      // Check if auth token exists and is valid
      const token = localStorage.getItem('auth_token');
      const user = localStorage.getItem('auth_user');
      
      if (!token) {
        return {
          message: 'No authentication token found',
          details: { authenticated: false, reason: 'no_token' }
        };
      }

      if (!user) {
        return {
          message: 'No user data found',
          details: { authenticated: false, reason: 'no_user_data' }
        };
      }

      // Try to validate token with API
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': this.getCurrentTenantId()
        }
      });

      if (response.ok) {
        return {
          message: 'Authentication is healthy',
          details: { 
            authenticated: true, 
            userId: JSON.parse(user).id,
            tokenValid: true 
          }
        };
      } else {
        return {
          message: 'Authentication token invalid',
          details: { 
            authenticated: false, 
            tokenValid: false,
            status: response.status 
          }
        };
      }
    } catch (error) {
      return {
        message: 'Authentication check failed',
        details: { 
          authenticated: false, 
          error: error.message 
        }
      };
    }
  }

  async checkDatabaseHealth() {
    try {
      const response = await fetch('/api/health/database', {
        headers: {
          'X-Tenant-ID': this.getCurrentTenantId()
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          message: 'Database connection is healthy',
          details: data
        };
      } else {
        return {
          message: 'Database connection failed',
          details: { status: response.status, statusText: response.statusText }
        };
      }
    } catch (error) {
      return {
        message: 'Database health check failed',
        details: { error: error.message }
      };
    }
  }

  async checkAPIHealth() {
    // Only check public endpoints that don't require authentication
    const endpoints = [
      '/api/health',
      '/api/health/database',
    ];

    const results = {};

    for (const endpoint of endpoints) {
      try {
        const startTime = performance.now();
        const response = await fetch(endpoint, {
          headers: {
            'X-Tenant-ID': this.getCurrentTenantId()
          }
        });
        const duration = performance.now() - startTime;

        results[endpoint] = {
          status: response.ok ? 'ok' : 'error',
          statusCode: response.status,
          responseTime: Math.round(duration)
        };
      } catch (error) {
        results[endpoint] = {
          status: 'error',
          error: error.message,
          responseTime: null
        };
      }
    }

    const healthyCount = Object.values(results).filter(r => r.status === 'ok').length;
    const allHealthy = healthyCount === endpoints.length;

    return {
      message: allHealthy ? 'All API endpoints healthy' : `${healthyCount}/${endpoints.length} endpoints healthy`,
      details: results
    };
  }

  async checkPerformanceHealth() {
    const metrics = {};

    // Core Web Vitals
    if (window.performance && window.performance.getEntriesByType) {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation) {
        metrics.domContentLoaded = Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart);
        metrics.loadComplete = Math.round(navigation.loadEventEnd - navigation.loadEventStart);
        metrics.firstPaint = this.getFirstPaint();
        metrics.firstContentfulPaint = this.getFirstContentfulPaint();
      }
    }

    // Memory usage
    if (performance.memory) {
      metrics.memoryUsage = {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) // MB
      };
    }

    // Network information
    if ('connection' in navigator) {
      metrics.connection = {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      };
    }

    // Performance thresholds
    const thresholds = {
      domContentLoaded: 1000,
      loadComplete: 3000,
      firstPaint: 1000,
      firstContentfulPaint: 1800,
      memoryUsage: 0.8 // 80% of limit
    };

    const issues = [];
    if (metrics.domContentLoaded > thresholds.domContentLoaded) {
      issues.push(`DOM content load too slow: ${metrics.domContentLoaded}ms`);
    }
    if (metrics.loadComplete > thresholds.loadComplete) {
      issues.push(`Page load too slow: ${metrics.loadComplete}ms`);
    }
    if (metrics.firstPaint > thresholds.firstPaint) {
      issues.push(`First paint too slow: ${metrics.firstPaint}ms`);
    }
    if (metrics.firstContentfulPaint > thresholds.firstContentfulPaint) {
      issues.push(`First contentful paint too slow: ${metrics.firstContentfulPaint}ms`);
    }
    if (metrics.memoryUsage && metrics.memoryUsage.used / metrics.memoryUsage.limit > thresholds.memoryUsage) {
      issues.push(`Memory usage too high: ${Math.round(metrics.memoryUsage.used / metrics.memoryUsage.limit * 100)}%`);
    }

    return {
      message: issues.length === 0 ? 'Performance is healthy' : `Performance issues detected: ${issues.length}`,
      details: { metrics, issues, thresholds }
    };
  }

  async checkSecurityHealth() {
    const checks = {
      https: window.location.protocol === 'https:',
      csp: !!document.querySelector('meta[http-equiv=content-security-policy]'),
      noMixedContent: !document.querySelector('img[src^="http:"], script[src^="http:"]'),
      secureCookies: this.checkSecureCookies(),
      noConsoleErrors: this.checkConsoleErrors()
    };

    const issues = Object.entries(checks)
      .filter(([_, passed]) => !passed)
      .map(([check]) => check);

    return {
      message: issues.length === 0 ? 'Security configuration is healthy' : `Security issues: ${issues.join(', ')}`,
      details: { checks, issues }
    };
  }

  async checkResourceHealth() {
    const resources = {};

    // Memory
    if (performance.memory) {
      resources.memory = {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit,
        usagePercent: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
      };
    }

    // Storage
    try {
      resources.storage = {
        localStorage: {
          used: new Blob(Object.values(localStorage)).size,
          available: 5 * 1024 * 1024 // 5MB estimated
        },
        sessionStorage: {
          used: new Blob(Object.values(sessionStorage)).size,
          available: 5 * 1024 * 1024 // 5MB estimated
        }
      };
    } catch (error) {
      resources.storage = { error: error.message };
    }

    // Performance timing
    if (performance.timing) {
      resources.timing = {
        dns: performance.timing.domainLookupEnd - performance.timing.domainLookupStart,
        tcp: performance.timing.connectEnd - performance.timing.connectStart,
        request: performance.timing.responseStart - performance.timing.requestStart,
        response: performance.timing.responseEnd - performance.timing.responseStart
      };
    }

    const issues = [];
    if (resources.memory && resources.memory.usagePercent > 80) {
      issues.push('High memory usage');
    }
    if (resources.storage && resources.storage.localStorage.used > resources.storage.localStorage.available * 0.8) {
      issues.push('Local storage nearly full');
    }

    return {
      message: issues.length === 0 ? 'Resource usage is healthy' : `Resource issues: ${issues.join(', ')}`,
      details: { resources, issues }
    };
  }

  async checkExternalServicesHealth() {
    const services = [
      { name: 'Sentry', url: '/api/monitoring/proxy/sentry' },
      { name: 'PostHog', url: '/api/monitoring/proxy/posthog' }
    ];

    const results = {};

    for (const service of services) {
      try {
        const startTime = performance.now();
        const response = await fetch(service.url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': this.getCurrentTenantId()
          }
        });
        const duration = performance.now() - startTime;

        if (response.ok) {
          const data = await response.json();
          results[service.name] = {
            status: 'healthy',
            responseTime: Math.round(duration),
            message: data.message || 'Service reachable',
            proxyUsed: true
          };
        } else {
          results[service.name] = {
            status: 'unhealthy',
            responseTime: Math.round(duration),
            error: `HTTP ${response.status}: ${response.statusText}`,
            proxyUsed: true
          };
        }
      } catch (error) {
        results[service.name] = {
          status: 'unreachable',
          error: error.message,
          proxyUsed: true
        };
      }
    }

    const healthyCount = Object.values(results).filter(r => r.status === 'healthy').length;

    return {
      message: `${healthyCount}/${services.length} monitoring services healthy via proxy`,
      details: results
    };
  }

  // Helper methods
  getFirstPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? Math.round(firstPaint.startTime) : null;
  }

  getFirstContentfulPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return fcp ? Math.round(fcp.startTime) : null;
  }

  checkSecureCookies() {
    // This is a simplified check - in reality, you'd need to examine actual cookies
    return document.cookie.includes('Secure') || document.cookie === '';
  }

  checkConsoleErrors() {
    // This would need to be implemented with error tracking
    // For now, return true (no obvious console errors)
    return true;
  }

  getCurrentTenantId() {
    try {
      const tenantData = JSON.parse(localStorage.getItem('tenant_context') || '{}');
      return tenantData.id || 'main';
    } catch (e) {
      return 'main';
    }
  }

  // Control methods
  start() {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    
    // Run initial check
    this.runAllChecks();
    
    // Set up periodic checks
    this.interval = setInterval(() => {
      this.runAllChecks();
    }, this.checkInterval);
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.isRunning = false;
  }

  getOverallHealth() {
    if (this.results.size === 0) {
      return {
        status: 'unknown',
        message: 'No health check results available',
        checks: {},
        timestamp: new Date().toISOString()
      };
    }

    const checks = Object.fromEntries(this.results);
    const criticalChecks = Object.values(checks).filter(check => check.critical);
    const unhealthyCritical = criticalChecks.filter(check => check.status !== 'healthy');
    const unhealthyChecks = Object.values(checks).filter(check => check.status !== 'healthy');

    let status = 'healthy';
    let message = 'All systems operational';

    if (unhealthyCritical.length > 0) {
      status = 'critical';
      message = `${unhealthyCritical.length} critical systems failing`;
    } else if (unhealthyChecks.length > 0) {
      status = 'degraded';
      message = `${unhealthyChecks.length} systems degraded`;
    }

    return {
      status,
      message,
      checks,
      summary: {
        total: Object.keys(checks).length,
        healthy: Object.values(checks).filter(c => c.status === 'healthy').length,
        degraded: Object.values(checks).filter(c => c.status === 'degraded').length,
        unhealthy: Object.values(checks).filter(c => c.status === 'unhealthy').length,
        critical: criticalChecks.length,
        criticalUnhealthy: unhealthyCritical.length
      },
      timestamp: new Date().toISOString()
    };
  }

  getHealthReport() {
    const overall = this.getOverallHealth();
    const checks = Object.fromEntries(this.results);
    
    return {
      overall,
      checks,
      metadata: {
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        environment: import.meta.env.MODE || 'development',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    };
  }
}

// Create and export singleton instance
const healthCheckSystem = new HealthCheckSystem();

export default healthCheckSystem;

// Export convenience methods
export const {
  registerCheck,
  runCheck,
  runAllChecks,
  getOverallHealth,
  getHealthReport,
  start,
  stop
} = healthCheckSystem;